/**
 * Шапка боковых панелей ПРЦ (новый расчёт, загрузка книги, модель, перечень
 * лимитов) — в той же манере, что у карточки модели справочника техники.
 */
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { fleetLeading, fleetType } from '@entities/fleet';
import { FieldError, FieldHint, FieldLabel } from '@widgets/FleetRegistry/ui';

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const Titles = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const Eyebrow = styled.p`
  margin: 0;
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

export const DrawerTitle = styled.h2`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  font-size: ${fleetType.title};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
  letter-spacing: -0.01em;
  text-wrap: balance;
  overflow-wrap: anywhere;

  /* Заголовок получает фокус при открытии, чтобы скринридер прочёл имя
     панели. Это не элемент управления — рамка фокуса ему не нужна. */
  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

export const HeaderMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  margin-top: ${({ theme }) => theme.spacing(2.5)};

  & > .text {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Поле формы панели: подпись, поле, подсказка и ошибка с правильными id. */
export function FormField({
  id,
  label,
  required,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <FieldBox>
      <FieldLabel htmlFor={id}>
        <span>
          {label}
          {required && (
            <span className="req" aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </span>
      </FieldLabel>
      {children}
      {hint && <FieldHint id={`${id}-hint`}>{hint}</FieldHint>}
      {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
    </FieldBox>
  );
}

/** `aria-describedby` поля: подсказка и ошибка, если есть. */
export function describedBy(id: string, hint: boolean, error: boolean): string | undefined {
  const ids = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

const FieldBox = styled.div`
  min-width: 0;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;
